import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as Colors from 'ui-library/Colors';

import {RenderTaskStatusWithIcon} from '../shared/ProcedureRunTableUtil';
import {MultiProcedureRunParamsTable} from './MultiProcedureRunParamsTable';
import {
  getUnitOperationCountByTaskStatus,
  PLANNED,
  UnitOperationStatusType,
  ProcedureRunOverview,
} from './useProcessDevelopmentStudyProgress';

export default function MultiExperimentProcessDevelopmentStudyExecutionProgress({
  overview,
  studyType,
}: {
  overview: ProcedureRunOverview;
  studyType: StudyType;
}): JSX.Element {
  // Track viewer state
  const [enabledStatusFilters, setEnabledStatusFilters] = useState(new Map());
  const setFilters = (statuses: Array<ProcessTaskStatusType>, isFilterSet: boolean): void => {
    const newEnabledStatusFilters = new Map(enabledStatusFilters);
    statuses.forEach((status) => newEnabledStatusFilters.set(status, isFilterSet));
    setEnabledStatusFilters(newEnabledStatusFilters);
  };

  // Calculate state values
  const statusCounts = getUnitOperationCountByTaskStatus(overview);

  return (
    <>
      <div className={css(styles.gridContainer)}>
        {/* ========== Body ========== */}
        <RenderStatusCountPane
          statuses={[PLANNED]}
          statusCounts={statusCounts}
          enabledStatusFilters={enabledStatusFilters}
          setFilters={setFilters}
        />
        <RenderStatusCountPane
          statuses={[ProcessTaskStatusType.PENDING]}
          statusCounts={statusCounts}
          enabledStatusFilters={enabledStatusFilters}
          setFilters={setFilters}
        />
        <RenderStatusCountPane
          statuses={[ProcessTaskStatusType.IN_PROGRESS]}
          statusCounts={statusCounts}
          enabledStatusFilters={enabledStatusFilters}
          setFilters={setFilters}
        />
        <RenderStatusCountPane
          statuses={[ProcessTaskStatusType.COMPLETED]}
          statusCounts={statusCounts}
          enabledStatusFilters={enabledStatusFilters}
          setFilters={setFilters}
        />
        <RenderStatusCountPane
          statuses={[
            ProcessTaskStatusType.INVALID,
            ProcessTaskStatusType.SKIPPED,
            ProcessTaskStatusType.FAILED,
            ProcessTaskStatusType.CANCELLED,
          ]}
          statusCounts={statusCounts}
          enabledStatusFilters={enabledStatusFilters}
          setFilters={setFilters}
        />
      </div>
      <MultiProcedureRunParamsTable
        overview={overview}
        studyType={studyType}
        enabledStatusFilters={enabledStatusFilters}
      />
    </>
  );
}

function RenderStatusCountPane({
  statuses,
  statusCounts,
  enabledStatusFilters,
  setFilters,
}: {
  statuses: Array<UnitOperationStatusType>;
  statusCounts: Map<UnitOperationStatusType, number>;
  enabledStatusFilters: Map<UnitOperationStatusType, boolean>;
  setFilters: (statuses: Array<UnitOperationStatusType>, isFilterSet: boolean) => void;
}): JSX.Element {
  const filterStatus = statuses.every((status) => enabledStatusFilters.get(status) ?? false);
  return (
    <div
      data-status-pane-id={statuses[0]}
      className={classNames(
        css(styles.roundedPane),
        css(styles.clickablePane),
        filterStatus ? css(styles.selectedPane) : null
      )}
      onClick={() => {
        setFilters(statuses, !filterStatus);
      }}
    >
      {statuses.length === 1 ? (
        <>
          <RenderTaskStatusWithIcon statusType={statuses[0]} />
          <div data-status-count-id={statuses[0]} className={css(styles.statusCount)}>
            {statusCounts.get(statuses[0]) ?? 0}
          </div>
        </>
      ) : (
        statuses.map((status) => (
          <div key={`status-count-${status}`} className={css(styles.miscStatusLine)}>
            <RenderTaskStatusWithIcon statusType={status} />
            <span data-status-count-id={status} className={css(styles.paneRight)}>
              {statusCounts.get(status) ?? 0}
            </span>
          </div>
        ))
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  loadingDiv: {
    paddingBottom: 5,
  },
  // Layout
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: 10,
    paddingBottom: 15,
  },
  // Cell Styles
  cellHeader: {
    verticalAlign: 'top',
    fontWeight: 'bold',
  },
  statusCount: {
    fontSize: 'x-large',
    paddingTop: 20,
  },
  roundedPane: {
    height: 'inherit',
    borderRadius: 4,
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 15,
    paddingRight: 15,
  },
  clickablePane: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: Colors.GRAY_LIGHT,
    },
  },
  selectedPane: {
    borderColor: Colors.BLUE,
    backgroundColor: Colors.BLUE_POWDER_LIGHT,
  },
  paneRight: {
    float: 'right',
    paddingLeft: 10,
  },
  plannedIcon: {
    paddingLeft: 2,
    paddingRight: 7,
  },
  miscStatusLine: {
    paddingBottom: 2,
  },
  statusText: {
    paddingLeft: 4,
  },
  paramsTableInCard: {
    width: '97%',
  },
});
